import type { RecurrenceFrequency } from "@myapp/contract";

/**
 * Client-side preview of a recurrence rule.
 *
 * This is a faithful mirror of `apps/api/src/modules/schedule/domain/recurrence.ts`. The server
 * stays authoritative — it expands the rule again on `POST /appointments` and `POST /tasks` — but
 * a preview that disagrees with the server is worse than no preview, so the two must not drift.
 *
 * TWO THINGS THIS FILE EXISTS TO GET RIGHT (both copied from the API implementation):
 *
 * 1. **Every occurrence is derived from `start`, never from the one before it.** For MONTHLY that
 *    is the whole ballgame: a series starting 31 Jan must yield 28 Feb and then *31* Mar. Stepping
 *    from the previous occurrence would clamp 31 → 28 in February and never recover, silently
 *    turning "monthly on the 31st" into "monthly on the 28th".
 *
 * 2. **Stepping happens in local time**, so a block stays at 13:00 wall-clock across a DST boundary
 *    even though its UTC instant shifts by an hour.
 *
 * The one deliberate difference: this returns flags instead of throwing. The API throws
 * `ScheduleSeriesInvalidRangeError` / `ScheduleSeriesTooManyOccurrencesError`; a form needs to
 * render a hint next to the field while the user is still typing the horizon.
 */

/** 5 years of weekly occurrences — same bound the API enforces. */
export const MAX_OCCURRENCES = 260;

const STEP_DAYS: Partial<Record<RecurrenceFrequency, number>> = {
  WEEKLY: 7,
  BIWEEKLY: 14,
};

export interface ExpandRecurrenceResult {
  /** Chronological, first occurrence included. Empty when `invalidRange` or `overflow`. */
  occurrences: Date[];
  /** The rule would produce more than `MAX_OCCURRENCES` — the API would answer 400. */
  overflow: boolean;
  /** `until` lands before `start`, or one of the dates is unparseable. */
  invalidRange: boolean;
}

const EMPTY_INVALID: ExpandRecurrenceResult = {
  occurrences: [],
  overflow: false,
  invalidRange: true,
};

export function expandRecurrence(input: {
  start: Date;
  frequency: RecurrenceFrequency;
  /** Inclusive horizon. Only the calendar date is used; the time of day comes from `start`. */
  until: Date;
}): ExpandRecurrenceResult {
  const { start, frequency } = input;

  if (Number.isNaN(start.getTime()) || Number.isNaN(input.until.getTime())) {
    return EMPTY_INVALID;
  }

  const horizon = endOfLocalDay(input.until);
  if (horizon.getTime() < start.getTime()) {
    return EMPTY_INVALID;
  }

  const occurrences: Date[] = [];
  for (let i = 0; ; i += 1) {
    const occurrence = nth(start, frequency, i);
    if (occurrence.getTime() > horizon.getTime()) break;

    occurrences.push(occurrence);
    if (occurrences.length > MAX_OCCURRENCES) {
      return { occurrences: [], overflow: true, invalidRange: false };
    }
  }

  // The loop cannot produce an empty list: i=0 is `start` itself, and the horizon is >= start.
  return { occurrences, overflow: false, invalidRange: false };
}

/** The i-th occurrence, always computed from `start` rather than from occurrence i-1. */
function nth(start: Date, frequency: RecurrenceFrequency, i: number): Date {
  const step = STEP_DAYS[frequency];

  if (step !== undefined) {
    const d = new Date(start.getTime());
    d.setDate(start.getDate() + i * step);
    return d;
  }

  // MONTHLY — same day of month, clamped to the last day of short months.
  const d = new Date(start.getTime());
  // Park on the 1st before changing month: setMonth() on the 31st would otherwise overflow into
  // the following month (31 Jan + 1 month = 3 Mar), which is a different bug entirely.
  d.setDate(1);
  d.setMonth(start.getMonth() + i);
  d.setDate(Math.min(start.getDate(), daysInMonth(d.getFullYear(), d.getMonth())));
  return d;
}

function daysInMonth(year: number, monthIndex: number): number {
  // Day 0 of the next month is the last day of this one.
  return new Date(year, monthIndex + 1, 0).getDate();
}

function endOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

/**
 * Parse the `YYYY-MM-DD` horizon a form produces into a local Date.
 *
 * `new Date('2026-05-31')` parses as UTC midnight, which is the 30th at 22:00 in Berlin — the
 * horizon would then read a day early everywhere. Build it from the parts instead.
 */
export function parseUntilDate(value: string): Date {
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return new Date(NaN);
  return new Date(y, m - 1, d);
}
